import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../../../UI/Btn'
import { PlainSelect } from '../../../../../../UI/PlainField'

import SettingHint from '../SettingHint'

import http from '../../../../../../shared/http'
import { TApi, TRoles } from '../../../../../../shared/const'
import {
	readSkills4RetailRequests,
	SKILLS4RETAIL_REQUESTS_OPTION
} from '../../../../../../shared/skills4RetailRequests'

import styles from '../style.module.scss'

/*
 * „Zuständig für Skills4Retail-Anfragen" (Anforderung #216).
 *
 * Wer im Hilfe-Formular „Skills4Retail" wählt, landet nicht bei der
 * Landesbetreuung seines Bundeslands, sondern bei einer Person der
 * Bundesebene, die alle diese Anfragen bearbeitet. Wer das ist, steht hier und
 * nicht im Code — eine Kennung im Code wäre auf Test und Prod verschieden und
 * bei einem Wechsel der Zuständigkeit eine Auslieferung.
 *
 * Zur Auswahl stehen nur Bundesadmins; der Server weist trotzdem nur zu, wenn
 * das Konto noch zur Verwaltung gehört. Leer heißt: gewohnter Weg über das
 * Bundesland.
 */
function Skills4RetailRequests(props) {
	const { t } = useTranslation()

	const [stand, setStand] = useState(() => readSkills4RetailRequests(props.form))
	const [personen, setPersonen] = useState([])
	const [projekte, setProjekte] = useState([])

	// Der Wert kommt nach, wenn die Einstellungen geladen sind.
	useEffect(() => setStand(readSkills4RetailRequests(props.form)), [props.form])

	useEffect(() => {
		let aktuell = true

		http.get(TApi.ADMIN_TICKET_ASSIGNEES, { disableToast: true })
			.then(({ data }) => {
				if (!aktuell) return
				setPersonen((data || []).filter(person => person.role === TRoles.FEDERAL_ADMIN))
			})
			.catch(() => aktuell && setPersonen([]))

		http.get(TApi.ADMIN_TICKET_PROJECTS, { disableToast: true })
			.then(({ data }) => aktuell && setProjekte(data || []))
			.catch(() => aktuell && setProjekte([]))

		return () => {
			aktuell = false
		}
	}, [])

	const personOptionen = personen.map(person => ({
		value: String(person.id),
		label: person.name ? `${person.name} (${person.email})` : person.email
	}))
	/*
	 * Steht in der Einstellung ein Konto, das nicht mehr in der Liste ist
	 * (gelöscht, herabgestuft), bleibt es als Zeile sichtbar — sonst zeigte die
	 * Auswahl „niemand", während gespeichert etwas anderes steht.
	 */
	if (stand.assigneeId && personen.length && !personen.some(person => person.id === stand.assigneeId)) {
		personOptionen.push({
			value: String(stand.assigneeId),
			label: t('Account #{{val1}} (no longer a federal admin)', { val1: stand.assigneeId })
		})
	}

	const projektOptionen = projekte.map(projekt => ({
		value: String(projekt.id),
		label: projekt.shortTitle ? `${projekt.title} (${projekt.shortTitle})` : projekt.title
	}))

	const onSubmit = event => {
		event.preventDefault()

		props
			.updateOptions({ name: SKILLS4RETAIL_REQUESTS_OPTION, value: stand })
			.then(() => toast.success(t('Options was updated')))
	}

	return (
		<form id={SKILLS4RETAIL_REQUESTS_OPTION} className={styles.root} onSubmit={onSubmit}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>{t('Responsible for Skills4Retail requests')}</h3>
				<SettingHint
					example={t(
						'Example: Viktoria Vidackovic is selected — a student asks about the third quiz in chapter 3 via the help button and chooses „Skills4Retail“. The request is assigned to Viktoria Vidackovic and booked to the project „Skills4Retail“; the state support of the student does not receive it.'
					)}
					warning={t(
						'Without a selection, Skills4Retail requests go to the responsible state support like every other request — the project is still set.'
					)}
				>
					{t(
						'In the help form, students and teachers can choose „Skills4Retail“ as a fourth topic. Such requests are assigned automatically to the person selected here, so that one person handles all of them across the federal states. The assignment appears in the history of the request, and the person receives the usual message about a new request. Supporters do not see this topic.'
					)}
				</SettingHint>
				<div className={styles.sectionContent}>
					<PlainSelect
						className={styles.field}
						label={t('Responsible person')}
						placeholder={t('Nobody — usual way via the federal state')}
						value={stand.assigneeId ? String(stand.assigneeId) : ''}
						options={personOptionen}
						onChange={event => {
							// Den Wert vorher herausnehmen: React 17 recycelt das
							// Ereignisobjekt, im Aktualisierer wäre er leer.
							const wert = event.target.value
							setStand(vorher => ({ ...vorher, assigneeId: wert ? Number(wert) : null }))
						}}
					/>

					<PlainSelect
						className={styles.field}
						label={t('Project')}
						placeholder={t('No project')}
						value={stand.projectId ? String(stand.projectId) : ''}
						options={projektOptionen}
						onChange={event => {
							// Den Wert vorher herausnehmen: React 17 recycelt das
							// Ereignisobjekt, im Aktualisierer wäre er leer.
							const wert = event.target.value
							setStand(vorher => ({ ...vorher, projectId: wert ? Number(wert) : null }))
						}}
					/>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

Skills4RetailRequests.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}

export default Skills4RetailRequests
